/**
 * ManageSubscriptiondetails Component
 *
 * Displays purchased subscription details for an Add-in service in a pure, reusable format.
 * Enterprise Group users can view their active subscriptions and manage them
 * through Renew and Stop actions, with specific visibility rules based on
 * subscription state and type.
 *
 * Features:
 * - Read-only subscription details display (Subscription Type, Frequency, Dates)
 * - Subscription Type (Auto-renewable / Non auto-renewable)
 * - Subscription Frequency (Monthly / Quarterly / Semi-Annual / Annual / One-Time)
 * - Effective From & To dates with trial handling
 * - Renew Subscription button (conditional visibility)
 * - Stop Subscription button (conditional visibility)
 * - Bundle Add-in support (single subscription for bundle)
 * - Trial period calculations and edge case handling
 * - Automatic data loading on mount
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { toast } from "react-toastify";
import moment from "moment";
import ApiServices from "../../services/ApiServices";
import { useLoading } from "../../hooks/useLoader";
import {
  EG_ADD_TO_CART,
  ENTERPRISE_ADDINS_SUBSCRIPTION_DETAILS,
  ENTERPRISE_ASSOCIATION_STOP,
} from "../../constants/api-constants";
import "./ManageSubscriptiondetails.css";
import { entityType } from "../../constants/common";
import { useHistory } from "react-router-dom";
import { RENEWAL_KEY } from "../Distributors/constants";

// ============================================================================
// CONSTANTS
// ============================================================================

/** Subscription frequency mapping for display */
const FREQUENCY_DISPLAY = {
  1: "Monthly",
  2: "Quarterly",
  3: "Semi-Annual",
  4: "Annual",
  99: "One-Time",
};

/** System standard date format */
const DATE_FORMAT = "MM/DD/YYYY";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Determines the subscription type display value based on auto-renewal status
 * @param {Object} subscription - Subscription object
 * @returns {string} Display value: "Auto-renewable" or "Non auto-renewable"
 */
const getSubscriptionTypeDisplay = (subscription) => {
  if (!subscription) return "N/A";

  // One-time subscriptions are always non-renewable
  if (subscription?.frequency === 99) {
    return "Non auto-renewable";
  }

  // Check auto-renewal status for recurring subscriptions
  const isAutoRenewable = subscription?.isAutoRenewable;
  return isAutoRenewable ? "Auto-renewable" : "Non auto-renewable";
};

/**
 * Determines if a subscription is currently expired
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if subscription is expired
 */
const isSubscriptionExpired = (subscription) => {
  return subscription?.isExpired || false;
};

/**
 * Determines if a subscription is currently in trial period
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if trial is active
 */
const isSubscriptionInTrial = (subscription) => {
  return (subscription?.hasTrial && subscription?.isInTrial) || false;
};

/**
 * Determines trial label if a subscription is currently in trial period
 * @param {Object} subscription - Subscription object
 * @returns {string} "Trial" if trial is active, otherwise empty string
 */
const determineTrialLabel = (subscription) => {
  return isSubscriptionInTrial(subscription) ? "Trial" : "";
};

/**
 * Calculates the trial end date
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Trial start date or null if no trial
 */
const getTrialStartDate = (subscription) => {
  if (!isSubscriptionInTrial(subscription)) return null;
  const fromDate = moment(subscription.trialStartDate);
  return fromDate;
};

/**
 * Calculates the trial end date
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Trial end date or null if no trial
 */
const getTrialEndDate = (subscription) => {
  if (!isSubscriptionInTrial(subscription)) return null;

  const toDate = moment(subscription.trialEndDate);

  return toDate;
};

/**
 * Determines if Renew button should be visible
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if Renew button should be shown
 */
const shouldShowRenewButton = (subscription) => {
  if (!subscription) return false;

  // Do not show for one-time subscriptions
  if (subscription.frequency === 99) {
    return false;
  }

  // Show if subscription is expired
  if (isSubscriptionExpired(subscription)) {
    return true;
  }

  // Show if subscription is non auto-renewable (and not expired)
  const isAutoRenewable = subscription?.isAutoRenewable;
  if (!isAutoRenewable && !isSubscriptionExpired(subscription)) {
    return true;
  }

  return subscription?.canRenew || false;
};

/**
 * Determines if Stop button should be visible
 * @param {Object} subscription - Subscription object
 * @returns {boolean} True if Stop button should be shown
 */
const shouldShowStopButton = (subscription) => {
  if (!subscription) return false;

  // Do not show for one-time subscriptions
  if (
    subscription.frequency === 99 ||
    subscription?.isAutoRenewable === false
  ) {
    return false;
  }

  // Do not show if subscription is expired
  if (isSubscriptionExpired(subscription)) {
    return false;
  }

  // if already stopped
  if (subscription?.isStopped) {
    return false;
  }

  // Show if subscription is still active
  return subscription?.canStop || false;
};

/**
 * Gets the effective from date, considering trial start
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Effective from date
 */
const getEffectiveFromDate = (subscription) => {
  // If trial exists, effective from is trial start date
  if (isSubscriptionInTrial(subscription)) {
    const trialStartDate = getTrialStartDate(subscription);
    return trialStartDate || "N/A";
  }
  // Otherwise it's the purchase date
  return moment(subscription.effectiveFromDate);
};

/**
 * Gets the effective to date, considering trial period
 * @param {Object} subscription - Subscription object
 * @returns {moment.Moment|null} Effective to date
 */
const getEffectiveToDate = (subscription) => {
  // If in trial, display trial end date
  if (isSubscriptionInTrial(subscription)) {
    const trialEndDate = getTrialEndDate(subscription);
    return trialEndDate || "N/A";
  }

  const effectiveToDate = moment(subscription.effectiveToDate);

  // After trial or no trial, display end of billing cycle
  return effectiveToDate;
};

const isValidMomentDate = (value) => {
  return moment.isMoment(value) && value.isValid();
};

const getStartOfDay = (value) => {
  if (!isValidMomentDate(value)) {
    return null;
  }

  return value.clone().startOf("day");
};

const canShowEffectiveToDate = (effectiveToDate, effectiveFromDate) => {
  if (!isValidMomentDate(effectiveToDate)) {
    return false;
  }

  const toDateStart = getStartOfDay(effectiveToDate);
  const todayStart = moment().startOf("day");

  if (!isValidMomentDate(effectiveFromDate)) {
    return toDateStart.isSameOrAfter(todayStart, "day");
  }

  const minAllowedDate = moment.max(todayStart, getStartOfDay(effectiveFromDate));
  return toDateStart.isSameOrAfter(minAllowedDate, "day");
};

// Get all renewal IDs
export const getRenewalIds = () => {
  try {
    return JSON.parse(localStorage.getItem(RENEWAL_KEY)) || [];
  } catch {
    localStorage.removeItem(RENEWAL_KEY);
    return [];
  }
};

// Add renewal ID (avoid duplicate)
export const addRenewalId = (enterpriseId, addInsId) => {
  const renewalIds = getRenewalIds();

  const exists = renewalIds.some(
    (item) => item.enterpriseId === enterpriseId && item.addInsId === addInsId,
  );

  if (!exists) {
    renewalIds.push({ enterpriseId, addInsId });
    localStorage.setItem(RENEWAL_KEY, JSON.stringify(renewalIds));
  }

  return renewalIds;
};

// Remove renewal ID
export const removeRenewalId = (enterpriseId, addInsId) => {
  let renewalIds = getRenewalIds();

  renewalIds = renewalIds.filter(
    (item) =>
      !(item.enterpriseId === enterpriseId && item.addInsId === addInsId),
  );

  localStorage.setItem(RENEWAL_KEY, JSON.stringify(renewalIds));
  return renewalIds;
};

// Check renewal exists
export const isRenewalExists = (enterpriseId, addInsId) => {
  const renewalIds = getRenewalIds();

  return renewalIds.some(
    (item) => item.enterpriseId === enterpriseId && item.addInsId === addInsId,
  );
};

// Check only addInsId exists
export const isAddInsSelected = (addInsId) => {
  const renewalIds = getRenewalIds();

  return renewalIds.some((item) => item.addInsId === addInsId);
};

export const isCartAddInExistsInLocalStorage = (cartItems = []) => {
  if (!cartItems.length) return false;

  return cartItems.some((item) => isAddInsSelected(item?.addIn?.addInId));
};

// Clear all
export const clearRenewalIds = () => {
  localStorage.removeItem(RENEWAL_KEY);
};

// check is subscription recurring
// const isRecurring = (subscription) => {
//   return subscription.frequency !== 99;
// }

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ManageSubscriptiondetails = ({
  addInId,
  isBundle = false,
  enterpriseGroupId = null,
}) => {
  const history = useHistory();
  // State management
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'stop' or 'renew'
  const [syncing, setSyncing] = useState(false);

  // Hooks
  const { showLoader } = useLoading();

  // Derive computed states
  const isExpired = useMemo(
    () => (subscription ? isSubscriptionExpired(subscription) : false),
    [subscription],
  );

  const showRenewButton = useMemo(
    () => (subscription ? shouldShowRenewButton(subscription) : false),
    [subscription],
  );

  const showStopButton = useMemo(
    () => (subscription ? shouldShowStopButton(subscription) : false),
    [subscription],
  );

  const canManageSubscription = !subscription?.isAccessProvidedByAdmin;

  const subscriptionTypeDisplay = useMemo(
    () => (subscription ? getSubscriptionTypeDisplay(subscription) : "N/A"),
    [subscription],
  );

  const frequencyDisplay = useMemo(
    () =>
      subscription ? FREQUENCY_DISPLAY[subscription.frequency] || "N/A" : "N/A",
    [subscription],
  );

  const effectiveFromDate = useMemo(
    () => (subscription ? getEffectiveFromDate(subscription) : null),
    [subscription],
  );

  const effectiveToDate = useMemo(
    () => (subscription ? getEffectiveToDate(subscription) : null),
    [subscription],
  );

  const hasValidEffectiveToDate = useMemo(
    () => canShowEffectiveToDate(effectiveToDate, effectiveFromDate),
    [effectiveToDate, effectiveFromDate],
  );

  /**
   * Fetches the subscription details from the API
   */
  const fetchSubscriptionDetails = useCallback(async () => {
    if (!addInId || !enterpriseGroupId) return;

    try {
      setLoading(true);

      const url = ENTERPRISE_ADDINS_SUBSCRIPTION_DETAILS(
        enterpriseGroupId,
        addInId,
      );
      const response = await ApiServices.httpGet(url);

      if (response?.responseStatus?.isSuccess) {
        setSubscription(response.result);
      } else {
        toast.error(
          response?.responseStatus?.description ||
            "Failed to load subscription details",
        );
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      toast.error(error?.message || "Error loading subscription details");
    } finally {
      setLoading(false);
    }
  }, [addInId, enterpriseGroupId]);

  /**
   * Initiates stop subscription action with confirmation
   */
  const handleStopSubscriptionClick = () => {
    setConfirmAction("stop");
    setConfirmDialogOpen(true);
  };

  /**
   * Confirms and executes stop subscription
   */
  const handleConfirmStopSubscription = async () => {
    setConfirmDialogOpen(false);

    try {
      setSyncing(true);
      showLoader(true);

      const url = ENTERPRISE_ASSOCIATION_STOP(enterpriseGroupId, addInId);
      // Make POST request to stop endpoint with query params
      const response = await ApiServices.httpPost(url);

      if (response?.responseStatus?.isSuccess) {
        // Update local state to reflect auto-renew disabled
        setSubscription((prev) => ({
          ...prev,
          autoRenewal: false,
        }));

        toast.success(
          response?.responseStatus?.description ||
            `Subscription will remain active until ${effectiveToDate?.format(DATE_FORMAT)}.`,
        );

        // Refresh subscription data after stop
        setTimeout(() => fetchSubscriptionDetails(), 1000);
      } else {
        toast.error(
          response?.responseStatus?.description ||
            "Failed to stop subscription",
        );
      }
    } catch (error) {
      console.error("Error stopping subscription:", error);
      toast.error(error?.message || "Error stopping subscription");
    } finally {
      setSyncing(false);
      showLoader(false);
    }
  };

  /**
   * Initiates renew subscription action
   */
  const handleRenewSubscriptionClick = () => {
    setConfirmAction("renew");
    setConfirmDialogOpen(true);
  };

  /**
   * Confirms and executes renew subscription
   */
  const handleConfirmRenewSubscription = async () => {
    setConfirmDialogOpen(false);

    try {
      setSyncing(true);
      showLoader(true);

      await AutohandleAddToCart();
    } catch (error) {
      console.error("Error initiating renewal:", error);
      toast.error(error?.message || "Error initiating renewal");
    } finally {
      setSyncing(false);
      showLoader(false);
    }
  };

  /**
   * Handles dialog cancellation
   */
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
  };

  // ============================================================================
  // Auto Cart and checkout flow for renewal
  // ============================================================================

  const AutohandleAddToCart = async () => {
    if (!addInId) {
      toast.error("Invalid add-in details");
      return;
    }

    const frequency = subscription?.frequency || 0;

    const payload = {
      itemId: addInId,
      quantity: 1,
      transactionType: 5,
      frequency: frequency,
      currency: "USD",
      entityType: entityType.ENTERPRISES_GROUP,
    };

    try {
      setLoading(true);
      const response = await ApiServices.httpPost(EG_ADD_TO_CART, payload);
      if (response?.responseStatus?.isSuccess) {
        console.log("Item added to cart successfully:", response);

        // Check if renewal ID already exists to prevent duplicates in cart
        const alreadyAdded = isRenewalExists(enterpriseGroupId, addInId);
        if (!alreadyAdded) {
          addRenewalId(enterpriseGroupId, addInId);
        }
        history.push("/cart/checkout");
      }
    } catch (err) {
      console.error("Error adding item to cart:", err);
      toast.error("Failed to add item to cart");
      return;
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscription on component mount
  useEffect(() => {
    if (addInId && enterpriseGroupId) {
      fetchSubscriptionDetails();
    }
  }, [addInId, enterpriseGroupId, fetchSubscriptionDetails]);

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          width="100%"
          style={{ padding: "24px" }}
        >
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (!subscription) {
      return (
        <Typography variant="body2" color="textSecondary">
          No subscription details available
        </Typography>
      );
    }

    return (
      <Grid container spacing={2} style={{ width: "100%" }}>
        {canManageSubscription && (
          <>
            {/* Subscription Type */}
            <Grid item xs={12} sm={6}>
              <Box className="detail-field">
                <Typography variant="body2" className="label">
                  Subscription Type
                </Typography>
                <Typography variant="body1" className="value">
                  {subscriptionTypeDisplay}
                </Typography>
              </Box>
            </Grid>

            {/* Subscription Frequency */}
            <Grid item xs={12} sm={6}>
              <Box className="detail-field">
                <Typography variant="body2" className="label">
                  Subscription Frequency
                </Typography>
                <Typography variant="body1" className="value">
                  {frequencyDisplay}
                </Typography>
              </Box>
            </Grid>
          </>
        )}

        {/* Effective From Date */}
        <Grid item xs={12} sm={6}>
          <Box className="detail-field">
            <Typography variant="body2" className="label">
              Effective From Date{" "}
              {determineTrialLabel(subscription) &&
                `(${determineTrialLabel(subscription)})`}
            </Typography>
            <Typography variant="body1" className="value">
              {effectiveFromDate?.format(DATE_FORMAT) || "N/A"}
            </Typography>
          </Box>
        </Grid>

        {/* Effective To Date */}
        {hasValidEffectiveToDate && (
          <Grid item xs={12} sm={6}>
            <Box className="detail-field">
              <Typography variant="body2" className="label">
                Effective To Date{" "}
                {determineTrialLabel(subscription) &&
                  `(${determineTrialLabel(subscription)})`}
              </Typography>
              <Typography variant="body1" className="value">
                {effectiveToDate?.format(DATE_FORMAT) || "N/A"}
              </Typography>
            </Box>
          </Grid>
        )}

        {subscription?.isAccessProvidedByAdmin && (
          <Grid item xs={12}>
            <Alert severity="info">
              Access Provided by Admin
            </Alert>
          </Grid>
        )}

        {/* Action Buttons */}
        {canManageSubscription && (showStopButton || showRenewButton) && (
          <Grid item xs={12}>
            <Box sx={{ borderTop: "1px solid #e0e0e0" }}>
              <Box className="action-buttons">
                {showRenewButton && (
                  <Button
                    fullWidth={false}
                    variant="contained"
                    color="primary"
                    onClick={handleRenewSubscriptionClick}
                    disabled={syncing}
                    className="renew-action-button"
                  >
                    {syncing ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Renew Subscription"
                    )}
                  </Button>
                )}

                {showStopButton && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleStopSubscriptionClick}
                    disabled={syncing}
                    className="stop-action-button"
                  >
                    {syncing ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Stop Subscription"
                    )}
                  </Button>
                )}

                {!showRenewButton && !showStopButton && isExpired && (
                  <Typography variant="body2" color="textSecondary">
                    This subscription has expired. Renew to continue using this
                    Add-in.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box className="manage-subscription-details">
      {renderContent()}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          {confirmAction === "stop"
            ? "Stop Subscription"
            : "Renew Subscription"}
        </DialogTitle>
        <DialogContent sx={{ marginTop: 2, textAlign: "center" }}>
          {confirmAction === "stop" ? (
            <Typography>
              Are you sure you want to stop this subscription? It will remain
              active until{" "}
              <strong>{effectiveToDate?.format(DATE_FORMAT)}</strong>, and no
              further payments will be collected from your account.
            </Typography>
          ) : (
            <Typography>
              Renew this subscription? You will be redirected to the checkout
              flow to complete the renewal process.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={handleConfirmDialogClose}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={
              confirmAction === "stop"
                ? handleConfirmStopSubscription
                : handleConfirmRenewSubscription
            }
            color={confirmAction === "stop" ? "error" : "primary"}
            variant="contained"
            disabled={syncing}
          >
            {confirmAction === "stop"
              ? "Stop Subscription"
              : "Renew Subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ManageSubscriptiondetails.propTypes = {
  addInId: PropTypes.number.isRequired,
  enterpriseGroupId: PropTypes.number,
  isBundle: PropTypes.bool,
};

ManageSubscriptiondetails.defaultProps = {
  enterpriseGroupId: null,
  isBundle: false,
};

export default ManageSubscriptiondetails;
